import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Archive, ArrowDown, ArrowUp, ArrowUpRight, ChevronDown, ChevronRight, Lock, MessageSquareText, Pencil, Plus } from "lucide-react";
import { Button, EmptyState, ErrorState, PageHeader, Pill, SelectField, Skeleton, TextArea, TextField, cx } from "../components/ui.tsx";
import { ConfirmDialog, Modal } from "../components/dialog.tsx";
import { StatusPill } from "../components/status.tsx";
import { GuardedLink } from "../components/plan-shell.tsx";
import { FilterPanel, OverflowMenu, type MenuItem } from "../components/nav-controls.tsx";
import { useCommand } from "./settings/shared.tsx";
import { ContextImpact, type Affected } from "../components/context-impact.tsx";
import { useToast } from "../components/toast.tsx";
import { ApiError, applyFieldErrors, get, patch, post } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import { useGuardedNavigate } from "../lib/guard.tsx";
import { usePageTitle, usePreference, useUrlState } from "../lib/nav.tsx";
import type { InitiativeDetail, InitiativeSummary, StructureAxis, StructureKR, StructureObjective } from "../lib/types.ts";
import { ReadonlyRows } from "../components/actions-readonly.tsx";
import { ProgressBar } from "../components/progress.tsx";
import { ReopenRequestDialog, type ReopenTarget } from "../components/reopen.tsx";
import { EDITABLE_STATUSES, guidance } from "../../shared/planning.ts";
import { formatDateBR } from "../../shared/dates.ts";
import { initiativeLabel, krLabel, objectiveLabel } from "../../shared/numbering.ts";
import { axisTones, type AxisTones } from "../../shared/colors.ts";
import { averageProgress, effectiveProgress } from "../../shared/progress.ts";
import { optionalText, requiredText } from "../../shared/setup.ts";
import { LIMITS } from "../../shared/limits.ts";

type Editing =
  | { kind: "objective"; axis: StructureAxis; item?: StructureObjective }
  | { kind: "kr"; objective: StructureObjective; item?: StructureKR };

export type Archiving = { kind: "objective" | "key_result" | "initiative"; id: string; version: number; title: string };

/** Locked objectives/KRs (holding in-review or validated work) can be the subject of a reopen request (BLD-22). */
const lockedBy = (inits: InitiativeSummary[]) => inits.some((i) => !i.archived_at && (i.status === "validated" || i.status === "in_review"));

/** Moves `id` one step within `ids`; returns null at the edges. */
const moved = (ids: string[], id: string, delta: -1 | 1) => {
  const i = ids.indexOf(id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= ids.length) return null;
  const next = [...ids];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
};

export function PlanningPage() {
  const plan = usePlan();
  usePageTitle("Planejamento", plan.name);
  const [f, setF] = useUrlState({ eixo: "", meus: "", arquivados: "" });
  const [collapsed, setCollapsed] = usePreference<string[]>(`tree:${plan.id}`, []);
  // Initiatives start closed; the ones opened in place are remembered per viewer (instructor feedback, pt 2).
  const [openInits, setOpenInits] = usePreference<string[]>(`tree-open:${plan.id}`, []);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [archiving, setArchiving] = useState<Archiving | null>(null);
  const [requesting, setRequesting] = useState<ReopenTarget | null>(null);
  const showArchived = plan.capabilities.can_manage && f.arquivados === "1";
  const q = useQuery({
    queryKey: planKey(plan.id, "structure", showArchived),
    queryFn: async () => (await get<{ data: StructureAxis[] }>(`/plans/${plan.id}/structure${showArchived ? "?include_archived=true" : ""}`)).data,
  });
  const { run } = useCommand(plan.id);
  const toggle = (id: string) => setCollapsed(collapsed.includes(id) ? collapsed.filter((x) => x !== id) : [...collapsed, id]);
  const toggleInit = (id: string) => setOpenInits(openInits.includes(id) ? openInits.filter((x) => x !== id) : [...openInits, id]);

  const reorder = (entity_type: string, parent_id: string | undefined, ids: string[], id: string, delta: -1 | 1) => {
    const next = moved(ids, id, delta);
    if (next) void run(() => post(`/plans/${plan.id}/reorder`, { entity_type, parent_id, ordered_ids: next }), "Ordem atualizada.");
  };

  const header = <PageHeader title="Planejamento da oficina" subtitle="Consulte o plano completo. Edite os eixos atribuídos aos seus grupos." />;
  if (q.isPending) return <>{header}<Skeleton lines={8} /></>;
  if (q.isError) return <>{header}<ErrorState error={q.error} onRetry={() => void q.refetch()} /></>;

  const axes = q.data.filter((a) => (!f.eixo || a.id === f.eixo) && (!f.meus || a.can_edit));
  const allIds = q.data.flatMap((a) => [a.id, ...a.objectives.map((o) => o.id)]);
  const chips = [
    ...(f.eixo ? [{ label: `Eixo: ${q.data.find((a) => a.id === f.eixo)?.name ?? "—"}`, onRemove: () => setF({ eixo: "" }) }] : []),
    ...(f.meus ? [{ label: "Somente meus eixos", onRemove: () => setF({ meus: "" }) }] : []),
    ...(showArchived ? [{ label: "Mostrando arquivados", onRemove: () => setF({ arquivados: "" }) }] : []),
  ];
  return (
    <>
      {header}
      <FilterPanel chips={chips} onClear={() => setF({ eixo: "", meus: "", arquivados: "" })}>
        <div className="flex flex-wrap items-end gap-x-5">
          <div className="w-full sm:w-72">
            <SelectField label="Eixo" value={f.eixo} onChange={(e) => setF({ eixo: e.target.value })}>
              <option value="">Todos os eixos</option>
              {q.data.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </SelectField>
          </div>
          <label className="my-3 flex min-h-10 items-center gap-2 text-sm">
            <input type="checkbox" className="size-4 accent-primary" checked={Boolean(f.meus)} onChange={(e) => setF({ meus: e.target.checked ? "1" : "" })} />
            Somente eixos que posso editar
          </label>
          {plan.capabilities.can_manage && (
            <label className="my-3 flex min-h-10 items-center gap-2 text-sm">
              <input type="checkbox" className="size-4 accent-primary" checked={showArchived} onChange={(e) => setF({ arquivados: e.target.checked ? "1" : "" })} />
              Mostrar arquivados
            </label>
          )}
          <div className="my-3 ml-auto flex gap-1">
            <Button variant="ghost" onClick={() => setCollapsed([])}>Expandir tudo</Button>
            <Button variant="ghost" onClick={() => { setCollapsed(allIds); setOpenInits([]); }}>Recolher tudo</Button>
          </div>
        </div>
      </FilterPanel>
      {q.data.length === 0 ? (
        <EmptyState title="Ainda não há eixos nesta oficina."
          action={plan.capabilities.can_manage && <GuardedLink href={`/planos/${plan.id}/configuracoes/eixos`} className="inline-flex min-h-10 items-center rounded-ctl bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark">Cadastrar eixos</GuardedLink>}>
          {plan.capabilities.can_manage ? "Os eixos organizam objetivos, KRs e iniciativas." : "O instrutor ainda está configurando os eixos."}
        </EmptyState>
      ) : axes.length === 0 ? (
        <EmptyState title="Nenhum eixo corresponde ao filtro." />
      ) : (
        <div className="space-y-5">
          {axes.map((axis) => (
            <AxisSection
              key={axis.id}
              axis={axis}
              axisIds={q.data.filter((a) => !a.archived_at).map((a) => a.id)}
              collapsed={collapsed}
              onToggle={toggle}
              openInits={openInits}
              onToggleInit={toggleInit}
              onRequest={setRequesting}
              onEdit={setEditing}
              onArchive={setArchiving}
              onReorder={reorder}
            />
          ))}
        </div>
      )}
      {editing && <HierarchyDialog editing={editing} onClose={() => setEditing(null)} />}
      {archiving && <ArchiveDialog item={archiving} onClose={() => setArchiving(null)} />}
      {requesting && <ReopenRequestDialog target={requesting} onClose={() => setRequesting(null)} />}
    </>
  );
}

function AxisSection({ axis, axisIds, collapsed, onToggle, openInits, onToggleInit, onRequest, onEdit, onArchive, onReorder }: {
  axis: StructureAxis;
  axisIds: string[];
  collapsed: string[];
  onToggle: (id: string) => void;
  openInits: string[];
  onToggleInit: (id: string) => void;
  onRequest: (t: ReopenTarget) => void;
  onEdit: (e: Editing) => void;
  onArchive: (a: Archiving) => void;
  onReorder: (entity: string, parent: string | undefined, ids: string[], id: string, delta: -1 | 1) => void;
}) {
  const plan = usePlan();
  const go = useGuardedNavigate();
  const open = !collapsed.includes(axis.id);
  const archived = Boolean(axis.archived_at);
  const edit = axis.can_edit && !archived;
  const tone = axisTones(axis.color);
  const askable = (inits: InitiativeSummary[], pending: boolean) => !plan.capabilities.can_manage && lockedBy(inits) && !pending;
  const editorCopy = plan.capabilities.can_manage ? "Você edita (instrutor)" : axis.can_edit ? "Seu grupo edita" : "Você pode consultar este eixo";
  const moveItems = (entity: string, parent: string | undefined, ids: string[], id: string): MenuItem[] => [
    { label: "Mover para cima", icon: <ArrowUp className="size-4" />, disabled: ids.indexOf(id) <= 0, onSelect: () => onReorder(entity, parent, ids, id, -1) },
    { label: "Mover para baixo", icon: <ArrowDown className="size-4" />, disabled: ids.indexOf(id) === ids.length - 1, onSelect: () => onReorder(entity, parent, ids, id, 1) },
  ];
  const liveIds = <T extends { id: string; archived_at: string | null }>(xs: T[]) => xs.filter((x) => !x.archived_at).map((x) => x.id);

  return (
    <section className={cx("rounded-panel border border-line bg-surface shadow-e1", archived && "opacity-70")} aria-labelledby={`axis-${axis.id}`}>
      <header className="flex flex-wrap items-center justify-end gap-2 rounded-t-panel border-b px-4 py-3 sm:px-5"
        style={{ borderLeft: `5px solid ${tone.base}`, background: tone.tint, borderBottomColor: tone.border }}>
        <button type="button" onClick={() => onToggle(axis.id)} aria-expanded={open} aria-controls={`axis-body-${axis.id}`}
          className="-ml-1 flex min-h-10 min-w-0 flex-1 basis-full items-center gap-2 rounded-ctl px-1 text-left hover:bg-white/60 sm:basis-0">
          {open ? <ChevronDown className="size-5 shrink-0 text-muted" aria-hidden /> : <ChevronRight className="size-5 shrink-0 text-muted" aria-hidden />}
          <span className="min-w-0">
            <span id={`axis-${axis.id}`} className="block text-lg font-bold" style={{ color: tone.text }}>{axis.name} {archived && <Pill tone="gray">Arquivado</Pill>}</span>
            <span className="block text-xs text-muted">
              {axis.editor_group_name ? `Grupo ${axis.editor_group_name}` : "Sem grupo editor (somente instrutor)"} · {editorCopy}
              {!open && ` · ${axis.objectives.length} objetivo(s)`}
            </span>
          </span>
        </button>
        {edit ? (
          <Button variant="secondary" onClick={() => onEdit({ kind: "objective", axis })}><Plus className="size-4" aria-hidden />Objetivo</Button>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-muted"><Lock className="size-3.5" aria-hidden />Somente consulta</span>
        )}
        {plan.capabilities.can_manage && !archived && <OverflowMenu label={`Opções do eixo ${axis.name}`} items={moveItems("axis", undefined, axisIds, axis.id)} />}
      </header>
      {open && (
        <div id={`axis-body-${axis.id}`} className="space-y-4 px-4 py-4 sm:px-5">
          {axis.objectives.length === 0 && <p className="text-sm text-muted">Ainda não há objetivos neste eixo.</p>}
          {axis.objectives.map((o) => {
            const oOpen = !collapsed.includes(o.id);
            const oArchived = Boolean(o.archived_at);
            const oEdit = edit && !oArchived;
            const krHint = guidance(o.key_results.filter((k) => !k.archived_at).length, "KRs");
            const krIds = liveIds(o.key_results);
            return (
              <div key={o.id} className={cx("rounded-ctl border", oArchived && "opacity-70")} style={{ borderColor: tone.border }}>
                <div className="flex flex-wrap items-start justify-end gap-2 rounded-t-ctl px-3 py-2.5" style={{ background: tone.tintStrong }}>
                  <button type="button" onClick={() => onToggle(o.id)} aria-expanded={oOpen}
                    className="flex min-h-10 min-w-0 flex-1 basis-full items-start gap-2 rounded-ctl px-1 text-left hover:bg-white/70 sm:basis-0">
                    {oOpen ? <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden /> : <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />}
                    <span className="min-w-0">
                      <span className="block text-[11px] font-bold tracking-wide uppercase" style={{ color: tone.text }}>{objectiveLabel(o.number)} {oArchived && "· arquivado"} {o.reopen_pending && <Pill tone="gold">Reabertura solicitada</Pill>}</span>
                      <span className="block font-semibold" style={{ color: tone.text }}>{o.title}</span>
                      {oOpen && o.description && <span className="block text-sm text-muted">{o.description}</span>}
                      {oOpen && krHint && !oArchived && <span className="mt-1 block text-xs text-muted">{krHint}</span>}
                      {!oOpen && <span className="block text-xs text-muted">{o.key_results.length} KR(s)</span>}
                    </span>
                  </button>
                  {oEdit && (
                    <>
                      <Button variant="ghost" onClick={() => onEdit({ kind: "kr", objective: o })}><Plus className="size-4" aria-hidden />KR</Button>
                      <OverflowMenu label={`Opções do objetivo ${o.title}`} items={[
                        { label: "Editar objetivo", icon: <Pencil className="size-4" />, onSelect: () => onEdit({ kind: "objective", axis, item: o }) },
                        ...(askable(o.key_results.flatMap((k) => k.initiatives), o.reopen_pending)
                          ? [{ label: "Solicitar reabertura", icon: <MessageSquareText className="size-4" />, onSelect: () => onRequest({ entity_type: "objective", entity_id: o.id, title: o.title }) }]
                          : []),
                        ...moveItems("objective", axis.id, liveIds(axis.objectives), o.id),
                        "separator",
                        { label: "Arquivar objetivo", danger: true, icon: <Archive className="size-4" />, onSelect: () => onArchive({ kind: "objective", id: o.id, version: o.version, title: o.title }) },
                      ]} />
                    </>
                  )}
                </div>
                {oOpen && (
                  <div className="divide-y divide-line">
                    {o.key_results.length === 0 && <p className="px-4 py-3 text-sm text-muted">Ainda não há KRs neste objetivo.</p>}
                    {o.key_results.map((k) => (
                      <KrBlock key={k.id} kr={k} objective={o} edit={oEdit && !k.archived_at} krIds={krIds} tone={tone}
                        openInits={openInits} onToggleInit={onToggleInit} onRequest={onRequest}
                        onEdit={onEdit} onArchive={onArchive} moveItems={moveItems} onNew={() => go(`/planos/${plan.id}/iniciativas/nova?kr=${k.id}`)} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function KrBlock({ kr: k, objective: o, edit, krIds, tone, openInits, onToggleInit, onRequest, onEdit, onArchive, moveItems, onNew }: {
  kr: StructureKR;
  objective: StructureObjective;
  edit: boolean;
  krIds: string[];
  tone: AxisTones;
  openInits: string[];
  onToggleInit: (id: string) => void;
  onRequest: (t: ReopenTarget) => void;
  onEdit: (e: Editing) => void;
  onArchive: (a: Archiving) => void;
  moveItems: (entity: string, parent: string | undefined, ids: string[], id: string) => MenuItem[];
  onNew: () => void;
}) {
  const plan = usePlan();
  const askable = (inits: InitiativeSummary[], pending: boolean) => !plan.capabilities.can_manage && lockedBy(inits) && !pending;
  const live = k.initiatives.filter((i) => !i.archived_at);
  const iHint = guidance(live.length, "iniciativas");
  // KR execution: average of its initiatives, planning-stage ones counting as 0 (BLD-17).
  const krProgress = live.some((i) => i.status === "validated") ? averageProgress(live.map(effectiveProgress)) : null;
  const initIds = live.map((i) => i.id);
  const initiativeMenu = (i: InitiativeSummary): (MenuItem | "separator")[] => [
    ...moveItems("initiative", k.id, initIds, i.id),
    "separator",
    {
      label: EDITABLE_STATUSES.includes(i.status) ? "Arquivar iniciativa" : "Arquivar (reabra antes)",
      danger: true,
      disabled: !EDITABLE_STATUSES.includes(i.status),
      icon: <Archive className="size-4" />,
      onSelect: () => onArchive({ kind: "initiative", id: i.id, version: i.version, title: i.title }),
    },
  ];
  return (
    <div className={cx("px-3 py-3 sm:px-4", k.archived_at && "opacity-70")}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold tracking-wide uppercase" style={{ color: tone.text }}>{krLabel(k.number)} {k.archived_at && "· arquivado"} {k.reopen_pending && <Pill tone="gold">Reabertura solicitada</Pill>}</p>
          <p className="font-medium" style={{ color: tone.text }}>{k.title}</p>
          {krProgress !== null && (
            <span className="mt-1 flex items-center gap-2 text-xs text-muted">
              Execução do {krLabel(k.number)}
              <ProgressBar value={krProgress} color={tone.base} label={`Execução do ${krLabel(k.number)}`} />
            </span>
          )}
          {k.description && <p className="text-sm text-muted">{k.description}</p>}
        </div>
        {edit && (
          <OverflowMenu label={`Opções do KR ${k.title}`} items={[
            { label: "Editar KR", icon: <Pencil className="size-4" />, onSelect: () => onEdit({ kind: "kr", objective: o, item: k }) },
            ...(askable(k.initiatives, k.reopen_pending)
              ? [{ label: "Solicitar reabertura", icon: <MessageSquareText className="size-4" />, onSelect: () => onRequest({ entity_type: "key_result", entity_id: k.id, title: k.title }) }]
              : []),
            ...moveItems("key_result", o.id, krIds, k.id),
            "separator",
            { label: "Arquivar KR", danger: true, icon: <Archive className="size-4" />, onSelect: () => onArchive({ kind: "key_result", id: k.id, version: k.version, title: k.title }) },
          ]} />
        )}
      </div>
      {k.initiatives.length === 0 ? (
        <div className="mt-2 rounded-ctl border border-dashed border-field px-3 py-3 text-sm">
          <p className="text-muted">Ainda não há iniciativas neste KR.</p>
          {edit && <Button className="mt-2" onClick={onNew}>Criar iniciativa neste KR</Button>}
        </div>
      ) : (
        <>
          <ul className="mt-2 space-y-2">
            {k.initiatives.map((i) => (
              <InitiativeRow key={i.id} initiative={i} color={tone.base} open={openInits.includes(i.id)} onToggle={() => onToggleInit(i.id)}
                menu={edit && !i.archived_at ? initiativeMenu(i) : null} />
            ))}
          </ul>
          {iHint && <p className="mt-1 text-xs text-muted">{iHint}</p>}
          {edit && <Button variant="ghost" className="mt-1" onClick={onNew}><Plus className="size-4" aria-hidden />Nova iniciativa</Button>}
        </>
      )}
    </div>
  );
}

/**
 * Initiative row (instructor feedback, pt 2): clicking it opens a compact read-only view of its actions in place and
 * clicking again closes it; "Abrir" goes to the full editor.
 */
function InitiativeRow({ initiative: i, color, open, onToggle, menu }: {
  initiative: InitiativeSummary;
  color: string;
  open: boolean;
  onToggle: () => void;
  menu: (MenuItem | "separator")[] | null;
}) {
  const plan = usePlan();
  const href = `/planos/${plan.id}/iniciativas/${i.id}`;
  return (
    <li className="flex items-start gap-1">
      <div className={cx("min-w-0 flex-1 overflow-hidden rounded-ctl border bg-surface transition-[border-color,box-shadow] duration-[var(--duration-slow)] ease-[var(--ease-soft)]", open ? "border-primary/40 shadow-e2" : "border-line shadow-e1 hover:border-line-strong hover:shadow-e2", i.archived_at && "opacity-70")}>
        <div className="flex items-stretch">
          <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={`init-peek-${i.id}`}
            aria-label={`${i.number ? `${initiativeLabel(i.number)}: ` : ""}${i.title}. ${open ? "Recolher" : "Ver ações"}`}
            className="elevate flex min-w-0 flex-1 items-start gap-2 px-3 py-2.5 text-left">
            {open ? <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden /> : <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />}
            <span className="min-w-0">
              {i.number && <span className="block text-[11px] font-bold tracking-wide text-muted uppercase">{initiativeLabel(i.number)}</span>}
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-ink">{i.title}</span>
                {i.archived_at ? <Pill tone="gray">Arquivada</Pill> : <StatusPill status={i.status} />}
                {i.revision_number && <span className="text-xs text-muted">Versão submetida {i.revision_number}</span>}
                {i.status === "validated" && !i.archived_at && <ProgressBar value={i.progress} color={color} label="Execução da iniciativa" />}
                {i.reopen_pending && <Pill tone="gold">Reabertura solicitada</Pill>}
              </span>
              <span className="mt-0.5 block text-xs text-muted">
                {i.unit_name ?? "Unidade não indicada"} · {i.action_count} ação(ões) · {i.weight_total}/100 pontos
                {i.starts_on && ` · ${formatDateBR(i.starts_on)} a ${formatDateBR(i.ends_on)}`}
              </span>
            </span>
          </button>
          <GuardedLink href={href} aria-label={`Abrir iniciativa ${i.title}`}
            className="elevate flex shrink-0 items-center gap-1 border-l border-line px-3 text-sm font-semibold text-primary">
            <span className="hidden sm:inline">Abrir</span><ArrowUpRight className="size-4" aria-hidden />
          </GuardedLink>
        </div>
        {open && <InitiativePeek id={i.id} href={href} />}
      </div>
      {menu && <div className="flex min-h-14 items-center"><OverflowMenu label={`Opções da iniciativa ${i.title}`} items={menu} /></div>}
    </li>
  );
}

function InitiativePeek({ id, href }: { id: string; href: string }) {
  const plan = usePlan();
  const q = useQuery({
    queryKey: planKey(plan.id, "initiative", id),
    queryFn: async () => (await get<{ data: InitiativeDetail }>(`/plans/${plan.id}/initiatives/${id}`)).data,
  });
  return (
    <div id={`init-peek-${id}`} className="border-t border-line px-3 pt-1 pb-3">
      {q.isPending ? <Skeleton lines={2} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : (
        <>
          {q.data.description && <p className="py-2 text-sm whitespace-pre-line text-muted">{q.data.description}</p>}
          <ReadonlyRows rows={q.data.actions} />
          <GuardedLink href={href} className="tap mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary">
            Abrir iniciativa completa<ArrowUpRight className="size-4" aria-hidden />
          </GuardedLink>
        </>
      )}
    </div>
  );
}

/** Archive with optional reason; the bottom-up rule (R23) comes back from the server as a clear message. */
export function ArchiveDialog({ item, onClose, onArchived }: { item: Archiving; onClose: () => void; onArchived?: () => void }) {
  const plan = usePlan();
  const { run, busy } = useCommand(plan.id);
  const [reason, setReason] = useState("");
  const path = { objective: "objectives", key_result: "key-results", initiative: "initiatives" }[item.kind];
  const noun = { objective: "objetivo", key_result: "KR", initiative: "iniciativa" }[item.kind];
  return (
    <ConfirmDialog open onOpenChange={(o) => !o && onClose()} title={`Arquivar ${noun}?`} confirmLabel="Arquivar" danger loading={busy}
      onConfirm={async () => {
        const ok = await run(async () => { await post(`/plans/${plan.id}/${path}/${item.id}/archive`, { expected_version: item.version, reason: reason || null }); return true; }, `${noun[0]!.toUpperCase()}${noun.slice(1)} arquivad${item.kind === "initiative" ? "a" : "o"}.`);
        if (ok) onArchived?.();
        onClose();
      }}>
      <p className="text-sm">“{item.title}” deixa de aparecer no planejamento e nos relatórios. Nada é apagado; o instrutor pode consultar os arquivados.</p>
      {item.kind !== "initiative" && <p className="mt-2 text-xs text-muted">Só é possível arquivar quando não há itens ativos dentro dele.</p>}
      <TextArea label="Motivo (opcional)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
    </ConfirmDialog>
  );
}

const hierarchyForm = z.object({
  title: requiredText(LIMITS.title, "Informe o título."),
  description: optionalText(LIMITS.longDescription),
});

function HierarchyDialog({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const plan = usePlan();
  const { run } = useCommand(plan.id);
  const [blocked, setBlocked] = useState<{ affected: Affected[]; changes: Record<string, string | null> } | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const item = editing.item;
  const isKr = editing.kind === "kr";
  const form = useForm<z.input<typeof hierarchyForm>>({
    resolver: zodResolver(hierarchyForm),
    defaultValues: { title: item?.title ?? "", description: item?.description ?? "" },
  });
  const submit = form.handleSubmit(async (v) => {
    const path = isKr ? "key-results" : "objectives";
    const ok = await run(
      () => item
        ? patch(`/plans/${plan.id}/${path}/${item.id}`, { ...v, expected_version: item.version })
        : post(`/plans/${plan.id}/${path}`, isKr ? { ...v, objective_id: editing.objective.id } : { ...v, axis_id: editing.axis.id }),
      item ? "Alteração salva." : isKr ? "KR criado." : "Objetivo criado.",
      (e: ApiError) => {
        if (e.code === "CONTEXT_REOPEN_REQUIRED") {
          setBlocked({ affected: (e.details?.affected as Affected[]) ?? [], changes: { title: v.title, description: v.description ?? null } });
          return true;
        }
        applyFieldErrors(e, form.setError as never);
        return e.fieldErrors.length > 0;
      },
    );
    if (ok) onClose();
  });
  const title = `${item ? "Editar" : "Novo"} ${isKr ? "KR" : "objetivo"}`;
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={title} wide
      footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button onClick={() => void submit()} loading={form.formState.isSubmitting}>Salvar</Button></>}>
      <form noValidate onSubmit={submit}>
        {blocked && item && (
          <ContextImpact
            planId={plan.id}
            isInstructor={plan.capabilities.can_manage}
            entityType={isKr ? "key_result" : "objective"}
            entityId={item.id}
            expectedVersion={item.version}
            current={item.title}
            changes={blocked.changes}
            affected={blocked.affected}
            onCancel={onClose}
            onDone={async () => {
              await qc.invalidateQueries({ queryKey: ["plan", plan.id] });
              toast("success", "Alteração aplicada. As iniciativas afetadas voltaram a rascunho.");
              onClose();
            }}
          />
        )}
        <TextField label={isKr ? "KR (resultado-chave)" : "Objetivo"} required autoFocus error={form.formState.errors.title?.message} {...form.register("title")} />
        <TextArea label="Descrição" rows={3} error={form.formState.errors.description?.message} {...form.register("description")} />
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}
